import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { TrendingUp } from "lucide-react";

/**
 * PropertyPulse — a REIT + retail-adjacent stock ticker for the property
 * managers who visit this site. Uses TradingView's free embeddable
 * ticker-tape widget (no API key required, official product, dark theme).
 *
 * Symbols chosen for our client persona:
 *   VNQ   — Vanguard Real Estate ETF (broad REITs)
 *   XLRE  — Real Estate Select Sector SPDR
 *   SPG   — Simon Property Group (retail REIT)
 *   O     — Realty Income Corp (net-lease REIT, "The Monthly Dividend Co.")
 *   PLD   — Prologis (industrial REIT)
 *   AMT   — American Tower (specialty REIT)
 *
 * Placement: subtle ticker strip in the footer / above closing CTA.
 * Message: "we speak your language, portfolio manager."
 */
export default function PropertyPulse({ variant = "strip" }) {
  const containerRef = useRef(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || el.dataset.mounted === "true") return;
    el.dataset.mounted = "true";

    const script = document.createElement("script");
    script.src = "https://s3.tradingview.com/external-embedding/embed-widget-ticker-tape.js";
    script.async = true;
    script.type = "text/javascript";
    script.innerHTML = JSON.stringify({
      symbols: [
        { proName: "AMEX:VNQ",  title: "REIT ETF" },
        { proName: "AMEX:XLRE", title: "Real Estate" },
        { proName: "NYSE:SPG",  title: "Simon Property" },
        { proName: "NYSE:O",    title: "Realty Income" },
        { proName: "NYSE:PLD",  title: "Prologis" },
        { proName: "NYSE:AMT",  title: "American Tower" },
        { proName: "FOREDATA:SP500", title: "S&P 500" },
      ],
      showSymbolLogo: true,
      isTransparent: true,
      displayMode: "compact",
      colorTheme: "dark",
      locale: "en",
    });
    el.appendChild(script);
  }, []);

  return (
    <section
      className="relative"
      style={{
        background: "linear-gradient(180deg, var(--bg) 0%, #050506 100%)",
        borderTop: "1px solid var(--line)",
      }}
      data-testid="property-pulse"
    >
      <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-14 py-8">
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="flex items-center gap-4 mb-5"
        >
          <TrendingUp size={13} style={{ color: "var(--gold)" }} strokeWidth={1.6} />
          <div className="font-mono text-[10px] uppercase tracking-[0.42em]" style={{ color: "var(--gold-2)" }}>
            Portfolio manager · Market pulse
          </div>
          <div className="flex-1 h-px" style={{ background: "linear-gradient(90deg, var(--line-gold), transparent)" }} />
          <div className="hidden md:block font-mono text-[9px] uppercase tracking-[0.32em]" style={{ color: "var(--text-3)" }}>
            REIT + real estate benchmarks · Live
          </div>
        </motion.div>

        {/* TradingView widget container */}
        <div
          ref={containerRef}
          className="tradingview-widget-container"
          data-testid="tradingview-widget"
          style={{
            borderRadius: "14px",
            overflow: "hidden",
            border: "1px solid var(--line)",
            background: "rgba(255,255,255,0.02)",
          }}
        >
          <div className="tradingview-widget-container__widget" />
        </div>

        <div className="mt-4 font-mono text-[9px] uppercase tracking-[0.36em] text-center" style={{ color: "var(--text-3)" }}>
          Not investment advice · Powered by TradingView
        </div>
      </div>
    </section>
  );
}
